import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, Scales, WarningCircle } from "@phosphor-icons/react";

/**
 * The Proportionality Gate — mandatory GDPR Art. 6(1)(f) balancing test.
 * Deliberate friction: cannot be dismissed by clicking outside; requires a
 * documented business purpose before any search is executed.
 */
export function ProportionalityGate({ open, onClose, target, purposes, otherMin, onConfirm, submitting }) {
  const [purpose, setPurpose] = useState("");
  const [justification, setJustification] = useState("");

  useEffect(() => {
    if (open) {
      setPurpose("");
      setJustification("");
    }
  }, [open, target]);

  const isOther = purpose === "OTHER";
  const otherValid = !isOther || justification.trim().length >= otherMin;
  const canSubmit = purpose && otherValid && !submitting;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent
        data-testid="proportionality-gate"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        className="bg-white border border-zinc-950 shadow-2xl rounded-sm sm:max-w-lg"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 flex items-center justify-center bg-zinc-950 rounded-sm">
              <Scales size={20} weight="duotone" className="text-white" />
            </div>
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              Proportionality Gate · Art. 6.1.f GDPR
            </span>
          </div>
          <DialogTitle className="font-heading text-2xl tracking-tight text-zinc-950">
            Motivazione della ricerca
          </DialogTitle>
          <DialogDescription className="text-zinc-600 leading-relaxed">
            Nessuna interrogazione viene eseguita senza una finalità legittima documentata.
            La tua scelta sarà registrata in modo <strong className="text-zinc-800">immutabile</strong> nel
            registro di audit.
          </DialogDescription>
        </DialogHeader>

        {target && (
          <div className="border border-zinc-200 bg-zinc-50 rounded-sm px-3 py-2 text-sm">
            <span className="text-zinc-500 text-xs uppercase tracking-wide font-mono">Soggetto</span>
            <div className="font-medium text-zinc-900" data-testid="gate-target-name">{target.label}</div>
            <div className="text-xs text-zinc-500 font-mono">{target.id}</div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide font-mono text-zinc-600">
              Finalità legittima *
            </Label>
            <Select value={purpose} onValueChange={setPurpose}>
              <SelectTrigger data-testid="gate-purpose-select" className="mt-1.5 border-zinc-300 rounded-sm">
                <SelectValue placeholder="Seleziona la finalità del trattamento…" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-zinc-200 shadow-lg">
                {purposes.map((p) => (
                  <SelectItem key={p.code} value={p.code} data-testid={`gate-purpose-${p.code}`}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isOther && (
            <div>
              <Label className="text-xs uppercase tracking-wide font-mono text-zinc-600">
                Specifica la finalità (min. {otherMin} caratteri) *
              </Label>
              <Textarea
                data-testid="gate-justification-input"
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                rows={3}
                placeholder="Descrivi in modo specifico e verificabile la finalità legittima…"
                className="mt-1.5 border-zinc-300 rounded-sm resize-none"
              />
              <div className={`text-xs mt-1 font-mono flex items-center gap-1 ${otherValid ? "text-green-700" : "text-zinc-500"}`}>
                {!otherValid && <WarningCircle size={13} weight="fill" className="text-amber-500" />}
                {justification.trim().length}/{otherMin} caratteri
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 pt-2 border-t border-zinc-100">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="gate-cancel-btn"
            className="text-zinc-600 hover:text-zinc-900 rounded-sm"
          >
            Annulla
          </Button>
          <Button
            onClick={() => onConfirm(purpose, justification)}
            disabled={!canSubmit}
            data-testid="gate-submit-btn"
            className="bg-zinc-950 text-white hover:bg-zinc-800 rounded-sm gap-2"
          >
            <ShieldCheck size={16} weight="bold" />
            {submitting ? "Registrazione…" : "Registra ed esegui"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
